/**
 * Sizing and layout resolution for the `NewsletterRegisterWidget` blok.
 *
 * All CMS values are raw CSS (`600px`, `300px`, `""` for auto) and are handed to
 * the component as custom properties rather than classes, so editors can add new
 * option values in Storyblok without a code change.
 */

type BoxLayout = {
  width: string
  marginLeft: string
  marginRight: string
  textAlign: string
}

/**
 * `centered` and `left` fill the container; the half strategies take half of it.
 * `desktopWidth` is applied as `max-width` on the same box, so the browser
 * resolves "whichever is smaller" on its own.
 *
 * `right-half` offsets by 50% rather than using `auto`, so the box still starts
 * at the container's centre line when `max-width` narrows it further.
 */
const BOX_LAYOUTS: Record<string, BoxLayout> = {
  centered: {
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    textAlign: 'center',
  },
  left: {
    width: '100%',
    marginLeft: '0',
    marginRight: 'auto',
    textAlign: 'left',
  },
  'left-half': {
    width: '50%',
    marginLeft: '0',
    marginRight: 'auto',
    textAlign: 'left',
  },
  'right-half': {
    width: '50%',
    marginLeft: '50%',
    marginRight: '0',
    textAlign: 'left',
  },
}

const DEFAULT_LAYOUT = 'centered'

export type NewsletterWidgetLayoutFields = {
  desktopWidth?: string
  desktopHeight?: string
  desktopLayout?: string
  mobileHeight?: string
  mobileLayout?: string
  backgroundColor?: { color?: string }
  textColor?: { color?: string }
}

export const resolveBoxLayout = (layout: string | undefined): BoxLayout =>
  BOX_LAYOUTS[layout || ''] ?? BOX_LAYOUTS[DEFAULT_LAYOUT]!

/**
 * A cleared richtext field still renders as `<p></p>`, so a truthy check on the
 * resolved HTML is not enough to tell whether an editor actually wrote anything.
 * Media tags count as content even though they carry no text.
 */
export const isRichtextEmpty = (html: string | null | undefined): boolean =>
  !html ||
  !html
    .replace(/<(img|svg|video|iframe)\b[^>]*>/gi, 'x')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim()

export const buildNewsletterWidgetCssVars = (
  fields: NewsletterWidgetLayoutFields,
): Record<string, string> => {
  const desktop = resolveBoxLayout(fields.desktopLayout)
  const mobile = resolveBoxLayout(fields.mobileLayout)
  const background = fields.backgroundColor?.color

  return {
    '--nrw-height-mobile': fields.mobileHeight || 'auto',
    '--nrw-height-desktop': fields.desktopHeight || 'auto',
    '--nrw-width-desktop': fields.desktopWidth || '100%',
    '--nrw-background': background || 'transparent',
    '--nrw-foreground': fields.textColor?.color || 'inherit',
    '--nrw-box-width-mobile': mobile.width,
    '--nrw-box-margin-left-mobile': mobile.marginLeft,
    '--nrw-box-margin-right-mobile': mobile.marginRight,
    '--nrw-box-text-align-mobile': mobile.textAlign,
    '--nrw-box-width-desktop': desktop.width,
    '--nrw-box-margin-left-desktop': desktop.marginLeft,
    '--nrw-box-margin-right-desktop': desktop.marginRight,
    '--nrw-box-text-align-desktop': desktop.textAlign,
  }
}
